import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { LearnerSlideModalShell } from './LearnerSlideModalShell';
import { useLearnerSlideModal } from '../../hooks/useLearnerSlideModal';

export type ExerciseLockedRequiredPlan = {
  id: number;
  name: string;
  description?: string;
};

export type LockedContentKind = 'Course' | 'Level' | 'Lesson' | 'Exercise' | 'Book';

export interface ExerciseLockedModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional; shown under the title when provided */
  exerciseTitle?: string;
  requiredPlans: ExerciseLockedRequiredPlan[];
  /** Drives the heading + body wording. Defaults to "Exercise" for backward compatibility. */
  contentKind?: LockedContentKind;
}

const HEADING_KEY: Record<LockedContentKind, string> = {
  Course: 'headingCourse',
  Level: 'headingLevel',
  Lesson: 'headingLesson',
  Exercise: 'headingExercise',
  Book: 'headingBook',
};

const KIND_KEY: Record<LockedContentKind, string> = {
  Course: 'kindCourse',
  Level: 'kindLevel',
  Lesson: 'kindLesson',
  Exercise: 'kindExercise',
  Book: 'kindBook',
};

const LOCKED_MODAL_PANEL_CLASS =
  'max-h-[min(90vh,640px)] w-full max-w-md border border-gray-200 bg-white text-center dark:border-gray-700 dark:bg-gray-800';

/**
 * Same UX as the lesson viewer when a learner opens an exercise they cannot access (subscription).
 * Reused by the global learner-search panel when clicking a locked search hit (course / level /
 * lesson / exercise) – `contentKind` switches the wording accordingly.
 * Open/close motion matches exercise/quiz slide modals (bottom → center → bottom).
 */
export const ExerciseLockedModal: React.FC<ExerciseLockedModalProps> = ({
  open,
  onClose,
  exerciseTitle,
  requiredPlans,
  contentKind = 'Exercise',
}) => {
  const { t } = useTranslation('learning');
  const [animationError, setAnimationError] = useState(false);
  const { mounted, visible, panelRef, requestClose, handleExitTransitionEnd } = useLearnerSlideModal(
    open,
    onClose,
    undefined,
    { historyBackId: 'exercise-locked' }
  );

  if (!mounted) return null;

  const kindLabel = t(`contentLocked.${KIND_KEY[contentKind]}`);
  const heading = t(`contentLocked.${HEADING_KEY[contentKind]}`);

  const shell = (
    <LearnerSlideModalShell
      mounted={mounted}
      visible={visible}
      panelRef={panelRef}
      onBackdropClick={requestClose}
      onPanelTransitionEnd={handleExitTransitionEnd}
      panelClassName={LOCKED_MODAL_PANEL_CLASS}
      ariaLabelledBy="exercise-locked-heading"
      align="center"
      zIndexClass="z-[100]"
    >
      <div className="overflow-y-auto p-6 sm:p-8">
        <div className="mx-auto mb-4 flex h-32 w-32 items-center justify-center sm:h-40 sm:w-40">
          {animationError ? (
            <Lock className="h-24 w-24 text-gray-400 dark:text-gray-500 sm:h-32 sm:w-32" aria-hidden />
          ) : (
            <DotLottieReact
              src="/animations/Locked_Icon.lottie"
              loop
              autoplay
              className="h-full w-full"
              onError={() => setAnimationError(true)}
            />
          )}
        </div>
        <h2 id="exercise-locked-heading" className="mb-2 text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">
          {heading}
        </h2>
        {exerciseTitle ? <p className="mb-2 text-sm font-medium text-gray-800 dark:text-gray-200">{exerciseTitle}</p> : null}
        <p className="mb-2 text-gray-600 dark:text-gray-300">
          {t('contentLocked.requiresPlan', { kind: kindLabel })}
        </p>
        {requiredPlans.length > 0 ? (
          <div className="mb-5 text-left">
            <p className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200 sm:text-base">
              {t('contentLocked.requiredPlans', { count: requiredPlans.length })}
            </p>
            <div className="space-y-2">
              {requiredPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20"
                >
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-200 sm:text-base">{plan.name}</p>
                  {plan.description ? (
                    <p className="mt-1 text-xs text-blue-700 dark:text-blue-300 sm:text-sm">{plan.description}</p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <p className="mb-5 text-sm text-gray-600 dark:text-gray-300">
          {requiredPlans.length > 0
            ? t('contentLocked.noPlanAccess', { count: requiredPlans.length, kind: kindLabel })
            : t('contentLocked.cannotAccess', { kind: kindLabel })}
        </p>
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent('app:navigate', { detail: { view: 'profile', hash: 'subscription' } })
              );
              requestClose();
            }}
            className="rounded-xl bg-blue-600 px-5 py-2.5 font-medium text-white transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            {t('contentLocked.goToPlans')}
          </button>
          <button
            type="button"
            onClick={requestClose}
            className="rounded-xl bg-gray-200 px-5 py-2.5 font-medium text-gray-800 transition-colors hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
          >
            {t('contentLocked.close')}
          </button>
        </div>
      </div>
    </LearnerSlideModalShell>
  );

  return typeof document !== 'undefined' ? createPortal(shell, document.body) : shell;
};
